/**
 * User offer – per-user offer letter (template id + modified template content)
 */

import { apiClient } from '../client'

export type UserOfferStatus = 'accepted' | 'declined'

export interface UserOffer {
  id: number
  user_id: number
  /** Job this offer is for (different applications / jobs = different rows). */
  job_id?: number | null
  offer_id: number
  template: string
  template_ar?: string | null
  status?: UserOfferStatus | string
  created_at: string
  updated_at: string
  user?: { id: number; en_name?: string; email?: string }
  offer?: { id: number; type?: string }
  job?: { id: number; title?: string | null }
}

interface UserOfferListResponse {
  data: UserOffer[]
  pagination: { total: number; page: number; limit: number; pages: number }
}

const unwrap = <T>(res: { data?: T }) => res.data

export const userOfferRepository = {
  /** Admin: list assigned user offers (paginated). */
  async getList(params: { page?: number; limit?: number; search?: string; user_id?: number }) {
    const searchParams = new URLSearchParams()
    if (params.page != null) searchParams.set('page', String(params.page))
    if (params.limit != null) searchParams.set('limit', String(params.limit))
    if (params.search?.trim()) searchParams.set('search', params.search.trim())
    if (params.user_id != null) searchParams.set('user_id', String(params.user_id))
    const res = await apiClient.get<{ data: UserOffer[]; pagination: UserOfferListResponse['pagination'] }>(
      `/api/user-offer/list?${searchParams.toString()}`
    )
    return {
      data: res.data ?? [],
      pagination: res.pagination ?? { total: 0, page: 1, limit: 10, pages: 0 },
    }
  },

  /** Logged-in user: all my offers (one per job). */
  async getMyOffers(): Promise<UserOffer[]> {
    try {
      const res = await apiClient.get<{ data: UserOffer[] }>('/api/user-offer')
      const d = res.data
      return Array.isArray(d) ? d : []
    } catch (_) {
      return []
    }
  },

  /** Logged-in user: offer for one job (`GET /api/user-offer?job_id=...`). */
  async getMyOfferForJob(jobId: number): Promise<UserOffer | null> {
    try {
      const qs = `?job_id=${encodeURIComponent(String(jobId))}`
      const res = await apiClient.get<{ data: UserOffer | null }>(`/api/user-offer${qs}`)
      const d = res.data
      return d && !Array.isArray(d) ? (d as UserOffer) : null
    } catch (_) {
      return null
    }
  },

  async getByUserId(userId: number, jobId?: number): Promise<UserOffer | null> {
    try {
      const qs = jobId != null ? `?job_id=${encodeURIComponent(String(jobId))}` : ''
      const res = await apiClient.get<{ data: UserOffer | null }>(`/api/user-offer/user/${userId}${qs}`)
      return res.data ?? null
    } catch (_) {
      return null
    }
  },

  /**
   * Logged-in user: accept or decline one offer.
   * Pass jobId to target the row (`job_id` in body and query). Omit only for legacy rows (job_id null).
   */
  async updateMyStatus(status: 'accepted' | 'declined', jobId?: number | null): Promise<UserOffer> {
    const body: { status: string; job_id?: number } = { status }
    if (jobId != null && !Number.isNaN(Number(jobId))) {
      body.job_id = Number(jobId)
    }
    const qs =
      jobId != null && !Number.isNaN(Number(jobId))
        ? `?job_id=${encodeURIComponent(String(Number(jobId)))}`
        : ''
    const res = await apiClient.patch<{ data: UserOffer }>(`/api/user-offer${qs}`, body)
    return unwrap(res)!
  },

  /**
   * Logged-in user: update offer HTML and/or status for a job. Always pass jobId for job-scoped offers.
   */
  async patchMyOffer(params: {
    jobId: number
    template?: string
    template_ar?: string
    status?: 'accepted' | 'declined'
  }): Promise<UserOffer> {
    const body: { job_id: number; template?: string; template_ar?: string; status?: string } = {
      job_id: params.jobId,
    }
    if (params.template !== undefined) body.template = params.template
    if (params.template_ar !== undefined) body.template_ar = params.template_ar
    if (params.status !== undefined) body.status = params.status
    const qs = `?job_id=${encodeURIComponent(String(params.jobId))}`
    const res = await apiClient.patch<{ data: UserOffer }>(`/api/user-offer${qs}`, body)
    return unwrap(res)!
  },

  async create(data: { user_id: number; job_id?: number; offer_id: number; template?: string; template_ar?: string }): Promise<UserOffer> {
    const res = await apiClient.post<{ data: UserOffer }>('/api/user-offer', data)
    return unwrap(res)!
  },

  async update(id: number, data: { user_id?: number; offer_id?: number; template?: string; template_ar?: string }): Promise<UserOffer> {
    const res = await apiClient.patch<{ data: UserOffer }>(`/api/user-offer/${id}`, data)
    return unwrap(res)!
  },
}
